import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Grid,
  Box,
  TextField,
  FormControl,
  MenuItem,
} from "@mui/material";
import { Breadcrumb, SimpleCard } from "app/components";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  LocalizationProvider,
  DateTimePicker,
} from "@mui/x-date-pickers";
import { Formik } from "formik";
import * as Yup from "yup";
import { CREATE_ANNOUNCEMENTS } from "app/redux/actions/announcements";
import { useAuth } from "app/hooks/useAuth";
import { toast } from "react-toastify";
import Loading from "app/components/MatxLoading";

const CreateAnnouncement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let auth = useAuth();

  const announcement = useSelector((state) => state.announcement)
  // ✅ Validation schema
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      title: Yup.string().required("Title is required."),
      message: Yup.string().required("Message is required."),
      type: Yup.string().required("Type is required."),
      targetRole: Yup.string().required("Target role is required."),
      expiresAt: Yup.date().nullable(),
    });
  }, []);

  // ✅ Submit handler
  const handleSubmit = (values) => {
    const payload = {
      title: values.title,
      message: values.message,
      type: values.type,
      targetRole: values.targetRole,
      expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
    };

    dispatch(CREATE_ANNOUNCEMENTS(payload, auth.token, moveToNext, moveToFailure));
  };
  const moveToNext = () => {
    toast.success("Announcement Created Successfully")
    navigate(-1)
  }
  const moveToFailure = (message) => {
    toast.error(message)
  }
  return (
    <SimpleCard>
      <Box>
        <Breadcrumb routeSegments={[{ name: "Create Announcement" }]} />
      </Box>
      <Box padding={2}>
        <Formik
          initialValues={{
            title: "",
            message: "",
            type: "General",
            targetRole: "All",
            expiresAt: null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
            submitCount,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Title */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Title *"
                    name="title"
                    fullWidth
                    value={values.title}
                    onChange={handleChange}
                    error={(touched.title || submitCount > 0) && Boolean(errors.title)}
                    helperText={(touched.title || submitCount > 0) && errors.title}
                  />
                </Grid>



                {/* Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Type *"
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      error={(touched.type || submitCount > 0) && Boolean(errors.type)}
                      helperText={(touched.type || submitCount > 0) && errors.type}
                    >
                      <MenuItem value="General">General</MenuItem>
                      <MenuItem value="Holiday">Holiday</MenuItem>
                      <MenuItem value="Urgent">Urgent</MenuItem>
                      <MenuItem value="Event">Event</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>

                {/* Target Role */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Target Role *"
                      name="targetRole"
                      value={values.targetRole}
                      onChange={handleChange}
                      error={(touched.targetRole || submitCount > 0) && Boolean(errors.targetRole)}
                      helperText={(touched.targetRole || submitCount > 0) && errors.targetRole}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="teamlead">Team Lead</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>

                {/* Expiry Date */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        label="Expires At"
                        value={values.expiresAt}
                        onChange={(date) => setFieldValue("expiresAt", date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: (touched.expiresAt || submitCount > 0) && Boolean(errors.expiresAt),
                            helperText: (touched.expiresAt || submitCount > 0) && errors.expiresAt,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>
                {/* Message */}
                <Grid item xs={12}>
                  <TextField
                    label="Message *"
                    name="message"
                    fullWidth
                    multiline
                    rows={4}
                    value={values.message}
                    onChange={handleChange}
                    error={(touched.message || submitCount > 0) && Boolean(errors.message)}
                    helperText={(touched.message || submitCount > 0) && errors.message}
                  />
                </Grid>
                {/* Submit */}
                {announcement.addLoading === true
                  ?
                  <Loading />
                  :
                  <Grid item xs={12}>
                    <Button variant="contained" type="submit">
                      Create Announcement
                    </Button>
                  </Grid>
                }
              </Grid>
            </form>
          )}
        </Formik>
      </Box>
    </SimpleCard>
  );
};

export default CreateAnnouncement;
